import { useState, useEffect, useRef } from 'preact/hooks';
import { Textarea, Group, ActionIcon, Stack, Collapse, Button } from '@mantine/core';
import { IconSend, IconPhoto } from '@tabler/icons-react';
import { ImageUpload } from './ImageUpload';
import { ImageFile } from '@/types/chrome-ai';
import { isValidImageFile, createImageFile } from '@/lib/chrome-ai';

interface ChatInputProps {
  onSendMessage: (message: string, images?: ImageFile[]) => void;
  disabled?: boolean;
  placeholder?: string;
  externalFiles?: File[];
  onExternalFilesProcessed?: () => void;
}

export function ChatInput({ 
  onSendMessage, 
  disabled = false, 
  placeholder = "Type your message...",
  externalFiles,
  onExternalFilesProcessed
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const [images, setImages] = useState<ImageFile[]>([]);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const processedFilesRef = useRef<File[]>([]);

  // Handle external files dropped on chat area
  useEffect(() => {
    if (externalFiles && externalFiles.length > 0) {
      // Check if these are new files (not already processed)
      const newFiles = externalFiles.filter(file => 
        !processedFilesRef.current.includes(file)
      );
      
      if (newFiles.length > 0) {
        // Process the files
        const validImages: ImageFile[] = [];
        newFiles.forEach(file => {
          if (isValidImageFile(file) && images.length + validImages.length < 5) {
            validImages.push(createImageFile(file));
          }
        });

        if (validImages.length > 0) {
          setImages(prev => [...prev, ...validImages]);
          setShowImageUpload(true); // Show the upload area
        }

        // Mark these files as processed
        processedFilesRef.current = externalFiles;
        
        // Notify parent that files were processed
        if (onExternalFilesProcessed) {
          onExternalFilesProcessed();
        }
      }
    }
  }, [externalFiles, onExternalFilesProcessed]);

  const handleSubmit = (e: Event) => {
    e.preventDefault();
    if ((message.trim() || images.length > 0) && !disabled) {
      onSendMessage(message.trim() || 'Please analyze these images:', images.length > 0 ? images : undefined);
      setMessage('');
      setImages([]);
      setShowImageUpload(false);
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <Stack gap="xs">
      <Collapse in={showImageUpload}>
        <ImageUpload
          images={images}
          onImagesChange={setImages}
          maxImages={5}
          disabled={disabled}
        />
      </Collapse>

      <form onSubmit={handleSubmit}>
        <Group gap="xs" align="flex-end">
          <ActionIcon
            variant="outline"
            size="lg"
            radius="xl"
            color={images.length > 0 ? 'blue' : 'gray'}
            onClick={() => setShowImageUpload(!showImageUpload)}
            disabled={disabled}
            style={{
              height: '42px',
              width: '42px',
            }}
          >
            <IconPhoto size={18} />
          </ActionIcon>

          <Textarea
            value={message}
            onChange={(e: any) => setMessage((e.target as HTMLTextAreaElement).value)}
            onKeyDown={handleKeyDown}
            placeholder={images.length > 0 ? "Ask a question about the images... (Shift+Enter for new line)" : `${placeholder} (Shift+Enter for new line)`}
            disabled={disabled}
            size="md"
            minRows={1}
            maxRows={6}
            autosize
            style={{ flex: 1 }}
            styles={{
              input: {
                borderRadius: '16px',
                paddingLeft: '16px',
                paddingRight: '16px',
                resize: 'none'
              }
            }}
          />
          
          <ActionIcon
            type="submit"
            size="lg"
            radius="xl"
            variant="filled"
            color="blue"
            disabled={disabled || (!message.trim() && images.length === 0)}
            style={{
              height: '42px',
              width: '42px',
            }}
          >
            <IconSend size={18} />
          </ActionIcon>
        </Group>
      </form>

      {images.length > 0 && (
        <Group gap="xs" justify="center">
          <Button
            variant="subtle"
            size="xs"
            color="blue"
            onClick={() => setShowImageUpload(true)}
          >
            {images.length} image{images.length !== 1 ? 's' : ''} selected
          </Button>
        </Group>
      )}
    </Stack>
  );
}